import {
  setApiOptions,
  setLoggingOptions,
  enableSentryLogger,
  enableSegmentLogger,
  enableHotjarLogger,
  setPreferenceDefaults,
  enablePreferencesFromDeviceStorage,
  createDefaultReduxStore,
  MockArrayData,
} from "@redriver/cinnamon-mui";
import fetchMock from "fetch-mock";
import pathToRegex from "path-to-regexp";
import { parseUrl } from "query-string";
import reducers from "./reducers";

export const setupApp = () => {
  configureApi();
  configureAuth();
  configureLogging();
  configurePreferences();
  configureMockApi();
  const store = configureStore();
  return { store };
};

// api / auth modules

const configureApi = () =>
  setApiOptions({
    apiUrl: process.env.API_URL,
  });

const configureAuth = () => {
  // docs app does not use authentication
};

// logging module

const configureLogging = () => {
  setLoggingOptions({
    environment: process.env.LOGGING_ENVIRONMENT,
    release: process.env.LOGGING_RELEASE,
  });
  if (process.env.SENTRY_DSN) {
    enableSentryLogger(process.env.SENTRY_DSN);
  }
  if (process.env.SEGMENT_APIKEY) {
    enableSegmentLogger(process.env.SEGMENT_APIKEY);
  }
  if (process.env.HOTJAR_SITEID) {
    enableHotjarLogger(process.env.HOTJAR_SITEID);
  }
};

// preferences module

const configurePreferences = () => {
  setPreferenceDefaults({
    timezone: "Europe/London",
  });
  enablePreferencesFromDeviceStorage(
    `${process.env.COOKIE_PREFIX}-preferences`
  );
};

// redux store module

const configureStore = () =>
  createDefaultReduxStore({
    reducers,
    reduxDevTools: process.env.REDUX_DEVTOOLS,
  });

// mock api

const configureMockApi = () => {
  const route = (path) => {
    const fn = (url) => {
      const fullPath = `${process.env.API_URL}${path}`;
      const parsedUrl = parseUrl(url).url;
      const regex = pathToRegex(fullPath);
      return regex.test(parsedUrl);
    };
    fn.toString = () => path;
    return fn;
  };

  const response =
    (response, delay = 50) =>
    () =>
      new Promise((resolve) => setTimeout(resolve, delay)).then(() => response);

  fetchMock
    // generic endpoints
    .get(
      route("api/endpoint"),
      response(() => 200, 500)
    )
    .post(
      route("api/endpoint"),
      response(() => 200, 500)
    )
    .get(
      route("api/slow-endpoint"),
      response(() => 200, 5000)
    )
    .post(
      route("api/slow-endpoint"),
      response(() => 200, 5000)
    )
    .get(
      route("api/paginated-endpoint"),
      response((url) => {
        const query = parseUrl(url).query ?? {};
        const { pageSize = 10, pageNumber = 1 } = query;
        const rows = new MockArrayData([
          { id: "1", name: "First" },
          { id: "2", name: "Second" },
          { id: "3", name: "Third" },
          { id: "4", name: "Fourth" },
          { id: "5", name: "Fifth" },
          { id: "6", name: "Sixth" },
          { id: "7", name: "Seventh" },
          { id: "8", name: "Eighth" },
          { id: "9", name: "Ninth" },
          { id: "10", name: "Tenth" },
          { id: "11", name: "Eleventh" },
          { id: "12", name: "Twelfth" },
          { id: "13", name: "Thirteenth" },
          { id: "14", name: "Fourteenth" },
          { id: "15", name: "Fifteenth" },
        ]);
        return rows.getPaged(pageSize, pageNumber);
      })
    )
    // lookup endpoint
    .get(
      route("api/lookup"),
      response((url) => {
        const query = parseUrl(url).query ?? {};
        const { search } = query;
        const colours = [
          { value: "red", label: "Red" },
          { value: "blue", label: "Blue" },
          { value: "green", label: "Green" },
          { value: "yellow", label: "Yellow" },
          { value: "cyan", label: "Cyan" },
          { value: "magenta", label: "Magenta" },
        ];
        if (!search) return colours;
        return colours.filter((c) =>
          c.label.toLowerCase().includes(search.toLowerCase())
        );
      }, 500)
    )
    // company data endpoints
    .get(
      route("api/companies"),
      response((url) => {
        const query = parseUrl(url).query ?? {};
        const { search } = query;
        const companies = [
          { id: "234", name: "Amazon", founded: 1994 },
          { id: "345", name: "Apple", founded: 1976 },
          { id: "456", name: "Google", founded: 1998 },
          { id: "567", name: "Microsoft", founded: 1975 },
          { id: "123", name: "Red River", founded: 2008 },
        ];
        if (!search) return companies;
        return companies.filter((c) =>
          c.name.toLowerCase().includes(search.toLowerCase())
        );
      }, 200)
    )
    .get(
      route("api/companies/:id"),
      response(
        () => ({
          id: "123",
          name: "Red River",
          brandColour: { value: "red", label: "Red" },
        }),
        1000
      )
    )
    .post(
      route("api/companies"),
      response(() => 200, 1000)
    )
    .put(
      route("api/companies/:id"),
      response(() => 200, 1000)
    )
    .spy();
};
